/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * A hook to manage the expandable state of a content container.
 * @param maxHeight - The maximum height of the content container.
 * @param dependency - A dependency to watch for changes.
 * @returns An object containing the container height, whether the content is overflowing, and the expandable state.
 */
export const useExpandable = (
  maxHeight: number | undefined,
  dependency: unknown,
) => {
  const ref = useRef<HTMLDivElement>(null);

  const [containerHeight, setContainerHeight] = useState(`${maxHeight}px`);
  const [isExpanded, setIsExpanded] = useState(maxHeight === undefined);
  const [hasOverflowingContent, setHasOverflowingContent] = useState(false);

  useEffect(() => {
    if ((ref.current?.scrollHeight ?? 0) <= (ref.current?.clientHeight ?? 0)) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, []);

  useLayoutEffect(() => {
    if (maxHeight === undefined) {
      return;
    }

    if ((ref.current?.clientHeight ?? 0) >= (ref.current?.scrollHeight ?? 0)) {
      setContainerHeight(`fit-content`);
      setHasOverflowingContent(false);
    } else {
      setContainerHeight(`${maxHeight}px`);
      setHasOverflowingContent(true);
    }
  }, [maxHeight, dependency]);

  const toggleExpand = () => {
    if (isExpanded) {
      setContainerHeight(`${maxHeight}px`);
      setIsExpanded(false);
    } else {
      setContainerHeight(`fit-content`);
      setIsExpanded(true);
    }
  };

  return {
    containerHeight,
    hasOverflowingContent,
    isExpanded,
    ref,
    toggleExpand,
  };
};
